import { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AddonService, AddonServiceCreateInput, AddonServiceUpdateInput, ServiceCategoryMinimum } from '@wrapforce/shared/schemas/pricing';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Select, SelectItem } from '@/components/ui/select';
import {
  useAddonServices,
  useCreateAddonService,
  useDeleteAddonService,
  useServiceCategoryMinimums,
  useShopRates,
  useUpdateAddonService,
  useUpdateShopRates,
  useUpsertServiceCategoryMinimum,
} from './api';

function centsToDollarString(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}

const CATEGORY_ORDER: ServiceCategoryMinimum['serviceCategory'][] = ['default', 'wrap', 'ppf', 'tint', 'ceramic', 'print'];
const CATEGORY_LABEL_KEYS: Record<ServiceCategoryMinimum['serviceCategory'], string> = {
  default: 'labourPolicies.minimums.category.default',
  wrap: 'labourPolicies.minimums.category.wrap',
  ppf: 'labourPolicies.minimums.category.ppf',
  tint: 'labourPolicies.minimums.category.tint',
  ceramic: 'labourPolicies.minimums.category.ceramic',
  print: 'labourPolicies.minimums.category.print',
};

export function LabourPoliciesTab({ workspaceId }: { workspaceId: string | null }) {
  const { confirm, ConfirmDialog } = useConfirm();

  return (
    <div className="flex flex-col gap-4">
      <InstallRateCard workspaceId={workspaceId} />
      <AddonServicesCard workspaceId={workspaceId} confirm={confirm} />
      <MinimumsCard workspaceId={workspaceId} />
      <ConfirmDialog />
    </div>
  );
}

function InstallRateCard({ workspaceId }: { workspaceId: string | null }) {
  const { data } = useShopRates(workspaceId);
  const updateShopRates = useUpdateShopRates(workspaceId);

  if (!data) return null;

  return <InstallRateForm key={data.versionId} resolved={data.resolved} updateShopRates={updateShopRates} />;
}

function InstallRateForm({
  resolved,
  updateShopRates,
}: {
  resolved: NonNullable<ReturnType<typeof useShopRates>['data']>['resolved'];
  updateShopRates: ReturnType<typeof useUpdateShopRates>;
}) {
  const { t } = useTranslation('pricingV2');
  const initialValue = (resolved.laborRateCentsPerHour / 100).toFixed(2);
  const [value, setValue] = useState(initialValue);

  const numericValue = Number(value);
  const isValid = value.trim() !== '' && Number.isFinite(numericValue) && numericValue >= 0;
  const isUnchanged = value === initialValue;

  async function handleSave() {
    try {
      await updateShopRates.mutateAsync({
        ...resolved,
        laborRateCentsPerHour: Math.round(numericValue * 100),
      });
      toast.success(t('labourPolicies.installRate.savedToast'));
    } catch {
      toast.error(t('labourPolicies.saveError'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('labourPolicies.installRate.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="labour-rate-input">{t('labourPolicies.installRate.label')}</Label>
            <Input
              id="labour-rate-input"
              data-testid="labour-rate-input"
              type="number"
              min={0}
              step="0.01"
              className="h-9 w-40"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <Button size="sm" disabled={!isValid || isUnchanged} loading={updateShopRates.isPending} onClick={handleSave}>
            {t('labourPolicies.installRate.save')}
          </Button>
        </div>
        <p className="text-xs text-muted">{t('labourPolicies.installRate.hint')}</p>
      </CardContent>
    </Card>
  );
}

function AddonServicesCard({
  workspaceId,
  confirm,
}: {
  workspaceId: string | null;
  confirm: ReturnType<typeof useConfirm>['confirm'];
}) {
  const { t } = useTranslation('pricingV2');
  const { data: services } = useAddonServices(workspaceId);
  const createService = useCreateAddonService(workspaceId);
  const updateService = useUpdateAddonService(workspaceId);
  const deleteService = useDeleteAddonService(workspaceId);

  const [addOpen, setAddOpen] = useState(false);
  const [editingService, setEditingService] = useState<AddonService | null>(null);

  const activeServices = (services ?? []).filter((s) => s.active);

  async function handleArchive(service: AddonService) {
    const confirmed = await confirm({
      title: t('labourPolicies.addonServices.archiveConfirmTitle'),
      description: t('labourPolicies.addonServices.archiveConfirmBody'),
      confirmLabel: t('labourPolicies.addonServices.archiveConfirmAction'),
      cancelLabel: t('labourPolicies.addonServices.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteService.mutateAsync(service.id);
      toast.success(t('labourPolicies.addonServices.archivedToast'));
    } catch {
      toast.error(t('labourPolicies.saveError'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('labourPolicies.addonServices.title')}</CardTitle>
        <Button size="sm" variant="secondary" className="ml-auto gap-1.5" onClick={() => setAddOpen(true)}>
          <Plus size={13} /> {t('labourPolicies.addonServices.add')}
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {activeServices.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted">{t('labourPolicies.addonServices.empty')}</p>
        ) : (
          activeServices.map((service) => (
            <div key={service.id} className="flex items-start justify-between gap-4 border border-border p-3">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-ink">{service.name}</span>
                <div className="flex gap-3 text-xs text-muted">
                  <span>
                    {service.pricingType === 'flat'
                      ? t('labourPolicies.addonServices.rowRateFlat', { amount: centsToDollarString(service.rateCents) })
                      : t('labourPolicies.addonServices.rowRateHourly', { amount: centsToDollarString(service.rateCents) })}
                  </span>
                  {service.estimatedMinutes !== null ? (
                    <span>{t('labourPolicies.addonServices.rowMinutes', { minutes: service.estimatedMinutes })}</span>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setEditingService(service)}
                  aria-label={t('labourPolicies.addonServices.form.save')}
                  className="text-muted hover:text-ink"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleArchive(service)}
                  className="text-xs font-semibold text-muted hover:text-ink"
                >
                  {t('labourPolicies.addonServices.archive')}
                </button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {addOpen ? (
        <AddonServiceDialog
          mode="add"
          onClose={() => setAddOpen(false)}
          onSave={async (input) => {
            try {
              await createService.mutateAsync(input as AddonServiceCreateInput);
              toast.success(t('labourPolicies.addonServices.addedToast'));
              setAddOpen(false);
            } catch {
              toast.error(t('labourPolicies.saveError'));
            }
          }}
          saving={createService.isPending}
        />
      ) : null}

      {editingService ? (
        <AddonServiceDialog
          mode="edit"
          service={editingService}
          onClose={() => setEditingService(null)}
          onSave={async (input) => {
            try {
              await updateService.mutateAsync({ id: editingService.id, input: input as AddonServiceUpdateInput });
              toast.success(t('labourPolicies.addonServices.updatedToast'));
              setEditingService(null);
            } catch {
              toast.error(t('labourPolicies.saveError'));
            }
          }}
          saving={updateService.isPending}
        />
      ) : null}
    </Card>
  );
}

function AddonServiceDialog({
  mode, service, onClose, onSave, saving,
}: {
  mode: 'add' | 'edit';
  service?: AddonService;
  onClose: () => void;
  onSave: (input: AddonServiceCreateInput | AddonServiceUpdateInput) => void;
  saving: boolean;
}) {
  const { t } = useTranslation('pricingV2');
  const [name, setName] = useState(service?.name ?? '');
  const [pricingType, setPricingType] = useState<'flat' | 'hourly'>(service?.pricingType ?? 'flat');
  const [rate, setRate] = useState(service ? centsToDollarString(service.rateCents) : '');
  const [minutes, setMinutes] = useState(service?.estimatedMinutes != null ? String(service.estimatedMinutes) : '');

  const canSave = name.trim() !== '' && rate.trim() !== '' && Number.isFinite(Number(rate)) && Number(rate) >= 0;

  function handleSave() {
    onSave({
      name: name.trim(),
      pricingType,
      rateCents: Math.round(Number(rate) * 100),
      estimatedMinutes: minutes.trim() === '' ? null : Number(minutes),
      sort: 0,
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={mode === 'add' ? t('labourPolicies.addonServices.addDialogTitle') : t('labourPolicies.addonServices.editDialogTitle')}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="addon-service-name">{t('labourPolicies.addonServices.form.name')}</Label>
          <Input
            id="addon-service-name"
            data-testid="addon-service-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('labourPolicies.addonServices.form.namePlaceholder')}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="addon-service-pricing-type">{t('labourPolicies.addonServices.form.pricingType')}</Label>
          <Select value={pricingType} onValueChange={(v) => setPricingType(v as 'flat' | 'hourly')}>
            <SelectItem value="flat">{t('labourPolicies.addonServices.form.pricingTypeFlat')}</SelectItem>
            <SelectItem value="hourly">{t('labourPolicies.addonServices.form.pricingTypeHourly')}</SelectItem>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="addon-service-rate">
            {pricingType === 'flat' ? t('labourPolicies.addonServices.form.rateFlat') : t('labourPolicies.addonServices.form.rateHourly')}
          </Label>
          <Input
            id="addon-service-rate"
            data-testid="addon-service-rate"
            type="number"
            min={0}
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="addon-service-minutes">{t('labourPolicies.addonServices.form.estimatedMinutes')}</Label>
          <Input
            id="addon-service-minutes"
            data-testid="addon-service-minutes"
            type="number"
            min={0}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder={t('labourPolicies.addonServices.form.estimatedMinutesPlaceholder')}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('labourPolicies.addonServices.cancel')}
          </Button>
          <Button type="button" loading={saving} disabled={!canSave} onClick={handleSave}>
            {t('labourPolicies.addonServices.form.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function MinimumsCard({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('pricingV2');
  const { data: minimums } = useServiceCategoryMinimums(workspaceId);
  const upsertMinimum = useUpsertServiceCategoryMinimum(workspaceId);

  const byCategory = new Map<ServiceCategoryMinimum['serviceCategory'], ServiceCategoryMinimum>();
  for (const m of minimums ?? []) byCategory.set(m.serviceCategory, m);

  async function handleMinPriceBlur(category: ServiceCategoryMinimum['serviceCategory'], value: string, current: ServiceCategoryMinimum | undefined) {
    if (value.trim() === '') return;
    const cents = Math.round(Number(value) * 100);
    if (cents === current?.minCents) return;
    try {
      await upsertMinimum.mutateAsync({ category, input: { minCents: cents, enabled: current?.enabled ?? false } });
    } catch {
      toast.error(t('labourPolicies.saveError'));
    }
  }

  async function handleEnabledChange(category: ServiceCategoryMinimum['serviceCategory'], enabled: boolean, current: ServiceCategoryMinimum | undefined) {
    try {
      await upsertMinimum.mutateAsync({ category, input: { minCents: current?.minCents ?? 0, enabled } });
    } catch {
      toast.error(t('labourPolicies.saveError'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('labourPolicies.minimums.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {CATEGORY_ORDER.map((category) => {
          const row = byCategory.get(category);
          return (
            <div key={category} className="flex items-end gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`min-price-${category}`}>
                  {t(CATEGORY_LABEL_KEYS[category])} · {t('labourPolicies.minimums.minPrice')}
                </Label>
                <Input
                  id={`min-price-${category}`}
                  data-testid={`min-price-${category}`}
                  type="number"
                  min={0}
                  step="0.01"
                  className="h-9 w-40"
                  defaultValue={row ? (row.minCents / 100).toFixed(2) : ''}
                  key={`${category}-${row?.minCents ?? 'none'}`}
                  onBlur={(e) => handleMinPriceBlur(category, e.target.value, row)}
                />
              </div>
              <label className="flex items-center gap-1.5 pb-2 text-xs font-semibold text-muted">
                <input
                  type="checkbox"
                  data-testid={`min-enabled-${category}`}
                  checked={row?.enabled ?? false}
                  onChange={(e) => handleEnabledChange(category, e.target.checked, row)}
                />
                {t('labourPolicies.minimums.enabled')}
              </label>
            </div>
          );
        })}
        <p className="text-xs text-muted">{t('labourPolicies.minimums.hint')}</p>
      </CardContent>
    </Card>
  );
}
